// src/Pages/AdminManagment/AdminList.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrash, FaSearch, FaTimes, FaPlus, FaLock, FaUsers, FaUserFriends } from 'react-icons/fa';
import { useDispatch, useSelector } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import { setState } from '../../redux/features/filtersSlice';
import { useGetAdminsQuery, useDeleteAdminMutation } from "../../redux/services/sabAdminService/admin.Services";
import ViewAdmin from '../SubAdminManagment/AdminView';
import EditAdmin from '../SubAdminManagment/AdminUpdate';

function AdminList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const filter = useSelector((state) => state.filters.admin || state.filters.state);
    const [statusFilter, setStatusFilter] = useState(filter.status || '');
    const [roleFilter, setRoleFilter] = useState(filter.role || '');

    const { data, isLoading, isError, refetch } = useGetAdminsQuery(filter);

    const adminData = data?.data || [];
    const pagination = data?.pagination || {};
    const totalPages = pagination?.totalPages || 0;
    const totalItems = pagination?.total || 0;
    const currentPage = pagination?.page || 1;
    const showPagination = totalPages > 1;

    const isActive = (status) => status === 1 || status === "1" || status === true;

    const getRoleBadge = (role) => {
        const roles = { 'admin': { label: 'Admin', color: 'primary' }, 'sub_admin': { label: 'Sub Admin', color: 'info' } };
        const r = roles[role] || { label: role, color: 'secondary' };
        return <span className={`badge bg-${r.color}`}>{r.label}</span>;
    };

    const handlePageChange = (page) => {
        dispatch(setState({ page }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSearch = (value) => {
        dispatch(setState({ search: value, page: 1 }));
    };

    const refetchData = () => {
        refetch();
        successToast("Data refreshed successfully!");
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        dispatch(setState({ status, page: 1 }));
    };

    const handleRoleFilter = (role) => {
        setRoleFilter(role);
        dispatch(setState({ role, page: 1 }));
    };

    const clearFilters = () => {
        setStatusFilter('');
        setRoleFilter('');
        dispatch(setState({ search: '', status: '', role: '', page: 1 }));
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        dispatch(setState({ limit: newLimit, page: 1 }));
    };

    const handlePermissions = (adminId) => {
        navigate(`/admin-permissions/${adminId}`);
    };

    // ✅ HANDLE STATUS CHANGE (Toggle Switch के लिए)
    const handleStatusChange = (adminId, currentStatus, adminName) => {
        dispatch(openModal({
            type: 'status-admin',
            title: 'Change Admin Status',
            data: {
                id: adminId,
                name: adminName,
                status: currentStatus,
                apiUrl: "/admin-status", // यह आपके बैकएंड के अनुसार होना चाहिए
                message: `Are you sure you want to change status of admin "${adminName}"?`,
                onSuccess: refetchData,
            }
        }));
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="mt-3">Loading Admins...</h5>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-5">
                <div className="alert alert-danger">
                    <h5>Something Went Wrong</h5>
                    <p>Unable to load admins. Please try again.</p>
                    <button className="btn btn-primary" onClick={refetchData}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* HEADER */}
            <div className="dashboard-header">
                <div className="dashboard-header-inner">

                    <div className="header-content">

                        <h2 className="page-title">
                            Admin Management

                            {totalItems > 0 && (
                                <span className="count-badge">
                                    {totalItems}
                                </span>
                            )}
                        </h2>

                        <div className="custom-breadcrumb">
                            <Link to="/">Home</Link>
                            <span>/</span>
                            <span className="active">Admin Management</span>
                        </div>
                    </div>

                    <button
                        onClick={() => navigate("/add-admin")}
                        className="btn btn-primary add-btn"
                    >
                        <FaPlus />
                        Add Sub Admin
                    </button>

                </div>
            </div>

          <div className="container-fluid">
              {/* SEARCH AND FILTER SECTION */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-center">
                        {/* Search Bar */}
                        <div className="col-md-4">
                            <SearchBar search={filter.search} setSearch={handleSearch} placeholder="Search by name, email..." />
                        </div>

                        {/* Role Filter */}
                        <div className="col-md-3">
                            <select className="form-select" value={roleFilter} onChange={(e) => handleRoleFilter(e.target.value)}>
                                <option value="">All Roles</option>
                                <option value="admin">Admin</option>
                                <option value="sub_admin">Sub Admin</option>
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div className="col-md-2">
                            <select className="form-select" value={statusFilter} onChange={(e) => handleStatusFilter(e.target.value)}>
                                <option value="">All Status</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        {/* Limit Filter */}
                        <div className="col-md-2">
                            <select className="form-select" value={filter.limit || 10} onChange={handleLimitChange}>
                                <option value={5}>5 per page</option>
                                <option value={10}>10 per page</option>
                                <option value={20}>20 per page</option>
                                <option value={50}>50 per page</option>
                            </select>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-md-1 text-md-end">
                            {(filter.search || filter.status || filter.role) && (
                                <button className="btn btn-outline-secondary" onClick={clearFilters}>
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            <div className="st-wrapper-card">
                {adminData.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="mb-3"><FaSearch className="text-muted" size={48} /></div>
                        <h5>No Admins Found</h5>
                        <p className="text-muted">Click 'Add Admin' to create your first admin</p>
                    </div>
                ) : (
                    <div className="st-table-responsive">
                        <table className="table st-custom-table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Mobile</th>
                                    <th>Role</th>
                                    <th>Waad</th>
                                    <th>Total Families</th>
                                    <th>Total Members</th>
                                    <th>Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {adminData.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>{(currentPage - 1) * (filter.limit || 10) + index + 1}</td>
                                        <td className="fw-semibold">{item?.name || 'N/A'}</td>
                                        <td>{item?.email || 'N/A'}</td>
                                        <td>{item?.mobile || 'N/A'}</td>
                                        <td>{getRoleBadge(item?.role)}</td>
                                        <td>{item?.waad_name || '-'}</td>

                                        {/* Clickable Links */}
                                        <td>
                                            {item.waad_id ? (
                                                <Link
                                                    to={`/waad-details/${item.waad_id}?type=pariwar`}
                                                    className="badge bg-info text-dark text-decoration-none"
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <FaUsers className="me-1" /> {item?.total_pariwar || 0}
                                                </Link>
                                            ) : (
                                                <span className="badge bg-secondary">0</span>
                                            )}
                                        </td>
                                        <td>
                                            {item.waad_id ? (
                                                <Link
                                                    to={`/waad-details/${item.waad_id}?type=sadasya`}
                                                    className="badge bg-primary text-white text-decoration-none"
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <FaUserFriends className="me-1" /> {item?.total_sadasya || 0}
                                                </Link>
                                            ) : (
                                                <span className="badge bg-secondary">0</span>
                                            )}
                                        </td>

                                        {/* Status Toggle Switch */}
                                        <td style={{ width: "150px" }}>
                                            <div className="city-status-switch">
                                                <input
                                                    type="checkbox"
                                                    id={`status-${item.id}`}
                                                    className="city-status-input"
                                                    checked={isActive(item.status)}
                                                    onChange={() =>
                                                        handleStatusChange(item.id, item.status, item.name)
                                                    }
                                                />
                                                <label htmlFor={`status-${item.id}`} className="city-status-label">
                                                    <span className="city-status-ball"></span>
                                                    <span className="city-status-text">
                                                        {isActive(item.status) ? "Active" : "Inactive"}
                                                    </span>
                                                </label>
                                            </div>
                                        </td>

                                        {/* 🟢 ACTION BUTTONS - BILKUL GOTRA JAISE */}
                                        <td className="text-center">
                                            {/* VIEW */}
                                            <button
                                                className="st-action-btn st-view-btn me-1"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "view-admin",
                                                            title: "View Admin Details",
                                                            data: item,
                                                        })
                                                    )
                                                }
                                                title="View Details"
                                            >
                                                <FaEye />
                                            </button>

                                            {/* EDIT */}
                                            <button
                                                className="st-action-btn st-edit-btn me-1"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "edit-admin",
                                                            title: "Edit Admin",
                                                            data: item,
                                                        })
                                                    )
                                                }
                                                title="Edit Admin"
                                            >
                                                <FaEdit />
                                            </button>

                                            {/* PERMISSION - सिर्फ Sub Admin के लिए */}
                                            {item?.role === 'sub_admin' && (
                                                <button
                                                    className="st-action-btn st-permission-btn me-1"
                                                    onClick={() => handlePermissions(item.id)}
                                                    title="Manage Permissions"
                                                >
                                                    <FaLock />
                                                </button>
                                            )}

                                            {/* DELETE */}
                                            <button
                                                className="st-action-btn st-delete-btn"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "delete-admin",
                                                            title: "Delete Admin",
                                                            data: {
                                                                id: item.id,
                                                                name: item.name,
                                                                apiUrl: "/admin-delete", // अपने बैकएंड के अनुसार
                                                                message: `Are you sure you want to delete admin "${item.name}"?`,
                                                                onSuccess: refetchData,
                                                            },
                                                        })
                                                    )
                                                }
                                                title="Delete Admin"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PAGINATION */}
                {showPagination && adminData.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center justify-content-between mt-4">
                        <div className="text-muted small mb-2 mb-md-0">
                            Showing {((currentPage - 1) * (filter.limit || 10)) + 1} to{' '}
                            {Math.min(currentPage * (filter.limit || 10), totalItems)} of {totalItems} entries
                        </div>
                        <Pagination page={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                    </div>
                )}
            </div>
          </div>
        </div>
    );
}

export default AdminList;